import Action from '@/services/Action/Action';
import * as types from '../mutation-types';
import dayjs from 'dayjs';
import Vue from 'vue';
import { forEach } from 'lodash';
import router from '@/router';

// state
export const state = {};

// mutations
export const mutations = {
  [types.FETCH_ACTION_FAILURE](state) {
    Object.assign(state, {});
  },

  [types.RESET_ACTION](state) {
    state = {};
  },

  [types.FETCHING_ACTION](state) {
    forEach(state, (value, key) => {
      Vue.delete(state, key);
    });

    Object.assign(state, {});
  },

  [types.FETCH_ACTION_SUCCESS](state, action) {
    forEach(state, (value, key) => {
      Vue.delete(state, key);
    });

    forEach(action, (value, key) => {
      Vue.set(state, key, value);
    });
  },

  [types.UPDATE_ACTION_FAILURE](state) {},

  [types.UPDATING_ACTION](state) {},

  [types.UPDATE_ACTION_SUCCESS](state, { field, value }) {
    Vue.set(state, field, value);
  },
};

// actions
export const actions = {
  /**
   * @param commit
   * @param id
   * @returns {Promise<void>}
   */
  fetchAction({ commit }, id) {
    return new Promise(async (resolve, reject) => {
      commit(types.FETCHING_ACTION);

      try {
        const response = await Action.find(id);
        commit(types.FETCH_ACTION_SUCCESS, response.data.data);
        resolve(response);
      } catch (e) {
        commit(types.FETCH_ACTION_FAILURE);
        reject(e);
      }
    });
  },

  refreshAction({ commit, state, dispatch }) {
    return dispatch('fetchAction', state.clientActionId);
  },

  /**
   * @param commit
   * @param field
   * @param value
   * @returns {Promise<void>}
   */
  async updateActionField({ commit }, { field, value }) {
    commit(types.UPDATING_ACTION);
    commit(types.UPDATE_ACTION_SUCCESS, { field, value });
    commit(types.UPDATE_ACTION_FAILURE);
  },

  /**
   * @param commit
   * @returns {Promise<void>}
   */
  updateTags({ commit }, { clientActionIds, tags }) {
    return new Promise((resolve, reject) => {
      commit(types.UPDATING_ACTION);

      const model = Vue.$model;

      model.tag(clientActionIds, tags)
        .then(() => {
          const value = tags.join(',');
          commit(types.UPDATE_ACTION_SUCCESS, { field: 'tags', value });
          resolve();
        })
        .catch(() => {
          commit(types.UPDATE_ACTION_FAILURE);
          reject();
        });
    });
  },

  /**
   * @param commit
   * @returns {Promise<void>}
   */
  updateWorkPlan({ commit }, { clientActionIds, workPlanId }) {
    return new Promise((resolve, reject) => {
      commit(types.UPDATING_ACTION);

      const model = Vue.$model;

      model.workPlan(clientActionIds, workPlanId)
        .then(() => {
          commit(types.UPDATE_ACTION_SUCCESS, { field: 'workPlanId', workPlanId });
          resolve();
        })
        .catch(() => {
          commit(types.UPDATE_ACTION_FAILURE);
          reject();
        });
    });
  },

  /**
   * @param commit
   * @returns {Promise<void>}
   */
  removeWorkPlan({ commit }, { clientActionIds }) {
    return new Promise((resolve, reject) => {
      commit(types.UPDATING_ACTION);

      const model = Vue.$model;

      model.removeWorkPlan(clientActionIds)
          .then(() => {
            commit(types.UPDATE_ACTION_SUCCESS, { field: 'workPlanId', workPlanId: null });
            resolve();
          })
          .catch(() => {
            commit(types.UPDATE_ACTION_FAILURE);
            reject();
          });
    });
  },

  /**
   * @param commit
   * @param clientActionIds
   * @param tags
   * @returns {Promise<void>}
   */
  removeTags({ commit }, { clientActionIds, tags }) {
    return new Promise((resolve, reject) => {
      commit(types.UPDATING_ACTION);

      Action.untag(clientActionIds, tags)
        .then((response) => {
          const value = response.data.data.join(',');
          commit(types.UPDATE_ACTION_SUCCESS, { field: 'tags', value });
          resolve(response);
        })
        .catch(() => {
          commit(types.UPDATE_ACTION_FAILURE);
          reject();
        });
    });
  },

  /**
   * @param commit
   * @returns {Promise<void>}
   */
  updateInternalDueDate({ commit }, { clientActionId, internalDueDate }) {
    return new Promise((resolve, reject) => {
      commit(types.UPDATING_ACTION);

      const formattedDate = internalDueDate ? dayjs(internalDueDate).format('YYYY-MM-DD') : null;
      const storeValue = internalDueDate ? dayjs(internalDueDate).toDate() : null;

      const params = {
        clientActionId,
        internalDueDate: formattedDate,
      };

      Action.updateInternalDueDate(params)
        .then((response) => {
          commit(types.UPDATE_ACTION_SUCCESS, { field: 'internalDueDate', value: storeValue });
          resolve(response);
        })
        .catch((e) => {
          commit(types.UPDATE_ACTION_FAILURE);
          reject(e);
        });
    });
  },

  /**
   * @param commit
   * @returns {Promise<void>}
   */
  updateDateReferredToLegal({ commit }, { clientActionId, dateReferredToLegal }) {
    return new Promise((resolve, reject) => {
      commit(types.UPDATING_ACTION);

      const formattedDate = dateReferredToLegal ? dayjs(dateReferredToLegal).format('YYYY-MM-DD') : null;
      const storeValue = dateReferredToLegal ? dayjs(dateReferredToLegal).toDate() : null;

      const params = {
        clientActionId,
        dateReferredToLegal: formattedDate,
      };

      Action.updateDateReferredToLegal(params)
        .then((response) => {
          commit(types.UPDATE_ACTION_SUCCESS, { field: 'dateReferredToLegal', value: storeValue });
          resolve(response);
        })
        .catch((e) => {
          commit(types.UPDATE_ACTION_FAILURE);
          reject(e);
        });
    });
  },

  /**
   * @param commit
   * @returns {Promise<void>}
   */
  updateExtendedDueDate({ commit }, { clientActionId, extendedDueDate }) {
    return new Promise((resolve, reject) => {
      commit(types.UPDATING_ACTION);

      const formattedDate = extendedDueDate ? dayjs(extendedDueDate).format('YYYY-MM-DD') : null;
      const storeValue = extendedDueDate ? dayjs(extendedDueDate).toDate() : null;

      const params = {
        clientActionId,
        extendedDueDate: formattedDate,
      };

      Action.updateExtendedDueDate(params)
        .then((response) => {
          commit(types.UPDATE_ACTION_SUCCESS, { field: 'extendedDueDate', value: storeValue });
          resolve(response);
        })
        .catch((e) => {
          commit(types.UPDATE_ACTION_FAILURE);
          reject(e);
        });
    });
  },

  /**
   * @param commit
   * @returns {Promise<void>}
   */
  updateWorkOrder({ commit }, { clientActionId, workOrder }) {
    return new Promise((resolve, reject) => {
      commit(types.UPDATING_ACTION);

      const params = {
        clientActionId,
        workOrder,
      };

      Action.updateWorkOrder(params)
        .then((response) => {
          const field = 'workOrder';
          const value = workOrder;
          commit(types.UPDATE_ACTION_SUCCESS, { field, value });
          resolve(response);
        })
        .catch((e) => {
          commit(types.UPDATE_ACTION_FAILURE);
          reject(e);
        });
    });
  },

  /**
   * Bulk update internal due dates for multiple actions
   * @param commit
   * @param clientActionIds
   * @param internalDueDate
   * @returns {Promise<void>}
   */
  bulkUpdateInternalDueDate({ commit }, { clientActionIds, internalDueDate }) {
    return new Promise((resolve, reject) => {
      commit(types.UPDATING_ACTION);

      Action.bulkUpdateInternalDueDate(clientActionIds, internalDueDate)
        .then((response) => {
          commit(types.UPDATE_ACTION_SUCCESS);
          resolve(response);
        })
        .catch((e) => {
          commit(types.UPDATE_ACTION_FAILURE);
          reject(e);
        });
    });
  },

};

// getters
export const getters = {};
